import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTachometerAlt,
  faBoxes,
  faClipboardList,
  faTicket,
  faUsers,
  faServer,
  faGear,
  faTrophy,
  faBullhorn,
  faBars,
  faTimes,
  faSignOutAlt,
  faSun,
  faMoon,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../ThemeContext';
import { useAuth } from '../../AuthContext';
import WhatsAppFloat from '../../components/WhatsAppFloat';
import './AdminLayout.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const BADGE_POLL = 3 * 60 * 1000;

const navItems = [
  { path: '/beast', label: 'Dashboard', icon: faTachometerAlt, end: true },
  { path: '/beast/services', label: 'Services', icon: faBoxes },
  { path: '/beast/orders', label: 'Orders', icon: faClipboardList, badgeKey: 'pending_orders' },
  { path: '/beast/tickets', label: 'Tickets', icon: faTicket, badgeKey: 'open_tickets' },
  { path: '/beast/users', label: 'Users', icon: faUsers },
  { path: '/beast/providers', label: 'Providers', icon: faServer },
  { path: '/beast/top-users', label: 'Top Users', icon: faTrophy },
  { path: '/beast/offers', label: 'Offers', icon: faBullhorn },
  { path: '/beast/settings', label: 'Settings', icon: faGear },
];

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const [badges, setBadges] = useState({ pending_orders: 0, open_tickets: 0 });
  const { theme, toggle } = useTheme();
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const fetchBadges = () => {
      fetch(`${API}/admin/badges`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((data) => setBadges({ pending_orders: data.pending_orders || 0, open_tickets: data.open_tickets || 0 }))
        .catch(() => {});
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, BADGE_POLL);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="admin-layout">
      <button className="admin-mobile-toggle" onClick={() => setOpen(!open)}>
        <FontAwesomeIcon icon={open ? faTimes : faBars} />
      </button>

      <div className={`admin-overlay ${open ? 'visible' : ''}`} onClick={() => setOpen(false)} />

      <aside className={`admin-sidebar ${open ? 'open' : ''}`}>
        <div className="admin-sidebar-logo">
          <img src="/Black and Yellow Online Store Logo.png" alt="bustisha" className="admin-logo-img" />
          <span className="admin-logo-text">Admin Panel</span>
        </div>

        <nav className="admin-sidebar-nav">
          {navItems.map((item) => {
            const count = item.badgeKey ? badges[item.badgeKey] || 0 : 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setOpen(false)}
              >
                <FontAwesomeIcon icon={item.icon} className="admin-nav-icon" />
                <span>{item.label}</span>
                {count > 0 && <span className="admin-nav-badge">{count > 99 ? '99+' : count}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="admin-theme-toggle" onClick={toggle}>
            <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
            <span>{theme === 'light' ? 'Dark' : 'Light'}</span>
          </button>
          <NavLink to="/" className="admin-back-btn">
            <span>bustisha</span>
          </NavLink>
          <button className="admin-logout-btn" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
      <WhatsAppFloat />
    </div>
  );
}
